import { useQueries } from '@tanstack/react-query';
import { profileComparisonApi } from '../api';
import type { ComparisonGroup } from '../../../../../shared/hooks';
import type { ProfileComparisonParams } from '../api/types';

const PROFILE_COMPARISON_KEY = 'profileComparison';

const byKey = <T>(groups: ComparisonGroup[], values: Array<T | undefined>): Record<string, T | undefined> => {
  const map: Record<string, T | undefined> = {};
  groups.forEach((group, index) => {
    map[group.key] = values[index];
  });
  return map;
};

export const useComparisonDemographics = (
  groups: ComparisonGroup[],
  granularity: ProfileComparisonParams['granularity'],
) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: ProfileComparisonParams = { ...group.filterParams, granularity };
      return {
        queryKey: [PROFILE_COMPARISON_KEY, 'demographics', params],
        queryFn: () => profileComparisonApi.getDemographics(params),
        staleTime: 5 * 60 * 1000,
        enabled: !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return {
    byKey: byKey(
      groups,
      results.map((r) => r.data?.data),
    ),
    isLoading: results.some((r) => r.isLoading),
  };
};

export const useComparisonHrv = (groups: ComparisonGroup[], granularity: ProfileComparisonParams['granularity']) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: ProfileComparisonParams = { ...group.filterParams, granularity };
      return {
        queryKey: [PROFILE_COMPARISON_KEY, 'hrv', params],
        queryFn: () => profileComparisonApi.getHrv(params),
        staleTime: 5 * 60 * 1000,
        enabled: !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return {
    byKey: byKey(
      groups,
      results.map((r) => r.data?.data),
    ),
    isLoading: results.some((r) => r.isLoading),
  };
};

export const useComparisonRecovery = (
  groups: ComparisonGroup[],
  granularity: ProfileComparisonParams['granularity'],
) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: ProfileComparisonParams = { ...group.filterParams, granularity };
      return {
        queryKey: [PROFILE_COMPARISON_KEY, 'recovery', params],
        queryFn: () => profileComparisonApi.getRecovery(params),
        staleTime: 5 * 60 * 1000,
        enabled: !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return {
    byKey: byKey(
      groups,
      results.map((r) => r.data?.data),
    ),
    isLoading: results.some((r) => r.isLoading),
  };
};

export const useComparisonStrain = (groups: ComparisonGroup[], granularity: ProfileComparisonParams['granularity']) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: ProfileComparisonParams = { ...group.filterParams, granularity };
      return {
        queryKey: [PROFILE_COMPARISON_KEY, 'strain', params],
        queryFn: () => profileComparisonApi.getStrain(params),
        staleTime: 5 * 60 * 1000,
        enabled: !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return {
    byKey: byKey(
      groups,
      results.map((r) => r.data?.data),
    ),
    isLoading: results.some((r) => r.isLoading),
  };
};

export const useComparisonSleep = (groups: ComparisonGroup[], granularity: ProfileComparisonParams['granularity']) => {
  const results = useQueries({
    queries: groups.map((group) => {
      const params: ProfileComparisonParams = { ...group.filterParams, granularity };
      return {
        queryKey: [PROFILE_COMPARISON_KEY, 'sleep', params],
        queryFn: () => profileComparisonApi.getSleep(params),
        staleTime: 5 * 60 * 1000,
        enabled: !!params.user_ids?.length || params.client_id !== undefined,
      };
    }),
  });

  return {
    byKey: byKey(
      groups,
      results.map((r) => r.data?.data),
    ),
    isLoading: results.some((r) => r.isLoading),
  };
};
